import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IntegrationHealthBadge } from "../components/shared/IntegrationHealth";
import {
  Database, Settings, Upload, Download, CheckCircle2, XCircle, Loader2,
  RefreshCw, FileText, Wrench, Calendar, Package, Clock, AlertTriangle, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";

function ImportCard({ title, icon: Icon, description, onImportSAP, onImportCSV, loading, lastResult }) {
  const fileRef = useRef(null);
  return (
    <div className="bg-white border border-[#E7E5E4] p-5" data-testid={`import-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <Icon size={20} className="text-[#D97706]" />
          <div>
            <h3 className="font-heading font-bold text-[#1C1917]">{title}</h3>
            <p className="text-xs text-[#78716C]">{description}</p>
          </div>
        </div>
      </div>
      <div className="flex gap-2 mb-3">
        <Button size="sm" onClick={onImportSAP} disabled={loading} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none flex-1" data-testid={`sap-import-${title.toLowerCase().replace(/\s/g, '-')}`}>
          {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Download size={14} className="mr-1" />}
          Import from SAP
        </Button>
        <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} disabled={loading} className="rounded-none flex-1 border-[#E7E5E4]" data-testid={`csv-import-${title.toLowerCase().replace(/\s/g, '-')}`}>
          <Upload size={14} className="mr-1" />CSV Upload
        </Button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => { if (e.target.files[0]) onImportCSV(e.target.files[0]); e.target.value = ""; }} />
      </div>
      {lastResult && (
        <div className="text-xs p-2 bg-[#FAFAF9] border border-[#E7E5E4]">
          <span className="text-[#10B981] font-bold">{lastResult.imported} imported</span>
          {lastResult.skipped > 0 && <span className="ml-2 text-[#78716C]">{lastResult.skipped} skipped</span>}
          {lastResult.failed > 0 && <span className="ml-2 text-[#DC2626]">{lastResult.failed} failed</span>}
        </div>
      )}
    </div>
  );
}

export default function SAPIntegrationPage({ onLogout, userRole, isFeatureEnabled }) {
  const [config, setConfig] = useState({});
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [exportHistory, setExportHistory] = useState([]);
  const [importLoading, setImportLoading] = useState({});
  const [importResults, setImportResults] = useState({});
  const [pushLoading, setPushLoading] = useState({});
  const [pushResults, setPushResults] = useState({});
  const [fullSyncing, setFullSyncing] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      const resp = await axios.get(`${API}/sap/config`, { headers: getAuthHeaders() });
      setConfig(resp.data);
      setForm(resp.data);
    } catch { /* empty */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const [impResp, expResp] = await Promise.all([
        axios.get(`${API}/sap/import-history`, { headers: getAuthHeaders() }),
        axios.get(`${API}/sap/export-history`, { headers: getAuthHeaders() }),
      ]);
      setHistory(impResp.data || []);
      setExportHistory(expResp.data || []);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { fetchConfig(); fetchHistory(); }, [fetchConfig, fetchHistory]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/sap/config`, form, { headers: getAuthHeaders() });
      toast.success("SAP configuration saved");
      fetchConfig();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const resp = await axios.post(`${API}/sap/test-connection`, {}, { headers: getAuthHeaders() });
      setTestResult(resp.data);
      if (resp.data.status === "connected") toast.success("SAP connection successful");
      else toast.error(resp.data.message || "Connection failed");
    } catch (err) { toast.error(err.response?.data?.detail || "Test failed"); setTestResult({ status: "error", message: "Request failed" }); }
    finally { setTesting(false); }
  };

  const handleSAPImport = async (type) => {
    setImportLoading((p) => ({ ...p, [type]: true }));
    try {
      const resp = await axios.post(`${API}/sap/import/${type}`, {}, { headers: getAuthHeaders() });
      setImportResults((p) => ({ ...p, [type]: resp.data }));
      toast.success(`${type}: ${resp.data.imported} records imported`);
      fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || `${type} import failed`); }
    finally { setImportLoading((p) => ({ ...p, [type]: false })); }
  };

  const handleCSVImport = async (type, file) => {
    setImportLoading((p) => ({ ...p, [type]: true }));
    const fd = new FormData();
    fd.append("file", file);
    try {
      const resp = await axios.post(`${API}/sap/import/csv/${type}`, fd, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
      setImportResults((p) => ({ ...p, [type]: resp.data }));
      toast.success(`CSV ${type}: ${resp.data.imported} records imported`);
      fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || `CSV import failed`); }
    finally { setImportLoading((p) => ({ ...p, [type]: false })); }
  };

  const handlePushToSAP = async (pushType, endpoint) => {
    setPushLoading((p) => ({ ...p, [pushType]: true }));
    try {
      const resp = await axios.post(`${API}/sap/export/${endpoint}`, {}, { headers: getAuthHeaders() });
      setPushResults((p) => ({ ...p, [pushType]: resp.data }));
      toast.success(`${pushType}: ${resp.data.pushed} records pushed to SAP`);
      fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || `Push failed`); }
    finally { setPushLoading((p) => ({ ...p, [pushType]: false })); }
  };

  const handleCSVExport = (type) => {
    window.open(`${API}/sap/export/csv/${type}`, "_blank");
  };

  const handleFullSync = async () => {
    setFullSyncing(true);
    try {
      const resp = await axios.post(`${API}/sap/sync/full`, {}, { headers: getAuthHeaders() });
      toast.success("Full bidirectional sync completed");
      setPushResults(resp.data.push || {});
      fetchHistory();
    } catch (err) { toast.error(err.response?.data?.detail || "Full sync failed"); }
    finally { setFullSyncing(false); }
  };

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="sap-integration-page">
        <div>
          <h1 className="font-heading font-black text-4xl text-[#1C1917] mb-2" data-testid="sap-title">SAP S/4HANA Integration</h1>
          <p className="text-[#78716C] text-sm">Connect to SAP to import equipment, materials, maintenance orders, and BOMs</p>
        </div>

        {/* Health Badge */}
        <IntegrationHealthBadge
          integration="sap"
          onTestConnection={async () => {
            const resp = await axios.post(`${API}/sap/test-connection`, {}, { headers: getAuthHeaders() });
            return resp.data;
          }}
        />

        {/* Connection Config */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="sap-config-section">
          <div className="flex items-center gap-3 mb-4">
            <Settings size={20} className="text-[#D97706]" />
            <h2 className="font-heading font-bold text-xl text-[#1C1917]">Connection Settings</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-[#1C1917]">SAP Server URL *</Label>
              <Input value={form.server_url || ""} onChange={(e) => setForm({ ...form, server_url: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-sm" placeholder="https://my-sap-system.company.com" data-testid="sap-server-url" />
            </div>
            <div>
              <Label className="text-[#1C1917]">Authentication Type</Label>
              <select value={form.auth_type || "basic"} onChange={(e) => setForm({ ...form, auth_type: e.target.value })} className="mt-1 w-full h-10 px-3 rounded-none border border-[#E7E5E4] bg-white text-sm" data-testid="sap-auth-type">
                <option value="basic">Basic Auth (Username/Password)</option>
                <option value="oauth2">OAuth 2.0 (Client Credentials)</option>
              </select>
            </div>
          </div>

          {form.auth_type === "basic" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="text-[#1C1917]">SAP Username</Label>
                <Input value={form.username || ""} onChange={(e) => setForm({ ...form, username: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono" data-testid="sap-username" />
              </div>
              <div>
                <Label className="text-[#1C1917]">SAP Password</Label>
                <Input type="password" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono" data-testid="sap-password" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label className="text-[#1C1917]">Token URL</Label>
                <Input value={form.token_url || ""} onChange={(e) => setForm({ ...form, token_url: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-sm" placeholder="https://.../oauth/token" data-testid="sap-token-url" />
              </div>
              <div>
                <Label className="text-[#1C1917]">Client ID</Label>
                <Input value={form.client_id || ""} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono" data-testid="sap-client-id" />
              </div>
              <div>
                <Label className="text-[#1C1917]">Client Secret</Label>
                <Input type="password" value={form.client_secret || ""} onChange={(e) => setForm({ ...form, client_secret: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono" data-testid="sap-client-secret" />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-[#78716C] text-xs">OData Base Path</Label>
              <Input value={form.odata_base_path || ""} onChange={(e) => setForm({ ...form, odata_base_path: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-xs" data-testid="sap-odata-path" />
            </div>
            <div>
              <Label className="text-[#78716C] text-xs">Equipment Service</Label>
              <Input value={form.equipment_service || ""} onChange={(e) => setForm({ ...form, equipment_service: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-xs" data-testid="sap-equip-service" />
            </div>
            <div>
              <Label className="text-[#78716C] text-xs">Material Service</Label>
              <Input value={form.material_service || ""} onChange={(e) => setForm({ ...form, material_service: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-xs" data-testid="sap-material-service" />
            </div>
            <div>
              <Label className="text-[#78716C] text-xs">Maintenance Service</Label>
              <Input value={form.maintenance_service || ""} onChange={(e) => setForm({ ...form, maintenance_service: e.target.value })} className="mt-1 rounded-none border-[#E7E5E4] font-mono text-xs" data-testid="sap-maint-service" />
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none" data-testid="sap-save-config">
              {saving ? "Saving..." : "Save Configuration"}
            </Button>
          </div>
        </div>

        {/* Import Section */}
        <div data-testid="sap-import-section">
          <div className="flex items-center gap-3 mb-4">
            <Database size={20} className="text-[#D97706]" />
            <h2 className="font-heading font-bold text-xl text-[#1C1917]">Data Import</h2>
          </div>
          <p className="text-sm text-[#78716C] mb-4">Import directly from SAP OData APIs or upload CSV exports as a fallback.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ImportCard
              title="Equipment"
              icon={Wrench}
              description="Machine master records → Vault machines"
              onImportSAP={() => handleSAPImport("equipment")}
              onImportCSV={(f) => handleCSVImport("equipment", f)}
              loading={importLoading.equipment}
              lastResult={importResults.equipment}
            />
            <ImportCard
              title="Materials"
              icon={Package}
              description="Spare parts & materials → Parts Catalog"
              onImportSAP={() => handleSAPImport("materials")}
              onImportCSV={(f) => handleCSVImport("materials", f)}
              loading={importLoading.materials}
              lastResult={importResults.materials}
            />
            <ImportCard
              title="Maintenance"
              icon={Calendar}
              description="PM orders & plans → Maintenance records"
              onImportSAP={() => handleSAPImport("maintenance")}
              onImportCSV={(f) => handleCSVImport("maintenance", f)}
              loading={importLoading.maintenance}
              lastResult={importResults.maintenance}
            />
            <ImportCard
              title="BOM"
              icon={FileText}
              description="Bills of Material → BOM registry"
              onImportSAP={() => handleSAPImport("bom")}
              onImportCSV={(f) => handleCSVImport("bom", f)}
              loading={importLoading.bom}
              lastResult={importResults.bom}
            />
          </div>
        </div>

        {/* CSV Template Info */}
        <div className="bg-[#FAFAF9] border border-[#E7E5E4] p-4" data-testid="csv-template-info">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-[#D97706] mt-0.5 shrink-0" />
            <div className="text-sm text-[#78716C]">
              <strong className="text-[#1C1917]">CSV Import Format Guide:</strong>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                <div>
                  <p className="font-medium text-[#44403C]">Equipment CSV columns:</p>
                  <code className="text-xs bg-white px-1">equipment, name, serial_number, asset_number, type, location, description</code>
                </div>
                <div>
                  <p className="font-medium text-[#44403C]">Materials CSV columns:</p>
                  <code className="text-xs bg-white px-1">material, description, manufacturer, manufacturer_part_number, vendor</code>
                </div>
                <div>
                  <p className="font-medium text-[#44403C]">Maintenance CSV columns:</p>
                  <code className="text-xs bg-white px-1">order, equipment, title, description, scheduled_date, status</code>
                </div>
                <div>
                  <p className="font-medium text-[#44403C]">BOM CSV columns:</p>
                  <code className="text-xs bg-white px-1">bom, material, plant, description, component, quantity, unit</code>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Full Sync Button */}
        <div className="bg-[#1C1917] p-6 text-white" data-testid="full-sync-section">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading font-bold text-xl">Full Bidirectional Sync</h2>
              <p className="text-[#A8A29E] text-sm mt-1">Pull all data from SAP, then push local changes back. Requires SAP connection.</p>
            </div>
            <Button onClick={handleFullSync} disabled={fullSyncing || !form.server_url} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none px-8" data-testid="full-sync-button">
              {fullSyncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
              {fullSyncing ? "Syncing..." : "Run Full Sync"}
            </Button>
          </div>
        </div>

        {/* Push to SAP Section */}
        <div data-testid="sap-export-section">
          <div className="flex items-center gap-3 mb-4">
            <Upload size={20} className="text-[#D97706]" />
            <h2 className="font-heading font-bold text-xl text-[#1C1917]">Push to SAP</h2>
          </div>
          <p className="text-sm text-[#78716C] mb-4">Push local Vault changes back to SAP via OData API, or download SAP-formatted CSV files.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-[#E7E5E4] p-5" data-testid="push-card-completions">
              <div className="flex items-center gap-3 mb-2">
                <CheckCircle2 size={18} className="text-[#10B981]" />
                <div>
                  <h3 className="font-heading font-bold text-[#1C1917]">Maintenance Completions</h3>
                  <p className="text-xs text-[#78716C]">Push completed orders back to SAP (TECO status)</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handlePushToSAP("completions", "maintenance-completions")} disabled={pushLoading.completions} className="bg-[#10B981] hover:bg-[#059669] text-white rounded-none flex-1" data-testid="push-completions">
                  {pushLoading.completions ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push to SAP
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleCSVExport("maintenance")} className="rounded-none border-[#E7E5E4]" data-testid="csv-export-maintenance">
                  <Download size={14} className="mr-1" />CSV
                </Button>
              </div>
              {pushResults.completions && <div className="text-xs mt-2 p-2 bg-[#F0FDF4] border border-[#10B981]"><span className="font-bold text-[#065F46]">{pushResults.completions.pushed} pushed</span>{pushResults.completions.failed > 0 && <span className="ml-2 text-[#DC2626]">{pushResults.completions.failed} failed</span>}</div>}
            </div>

            <div className="bg-white border border-[#E7E5E4] p-5" data-testid="push-card-new-maint">
              <div className="flex items-center gap-3 mb-2">
                <Calendar size={18} className="text-[#2563EB]" />
                <div>
                  <h3 className="font-heading font-bold text-[#1C1917]">New Maintenance Orders</h3>
                  <p className="text-xs text-[#78716C]">Create Vault orders in SAP</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handlePushToSAP("new_maintenance", "new-maintenance")} disabled={pushLoading.new_maintenance} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none flex-1" data-testid="push-new-maint">
                  {pushLoading.new_maintenance ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push to SAP
                </Button>
              </div>
              {pushResults.new_maintenance && <div className="text-xs mt-2 p-2 bg-[#EFF6FF] border border-[#2563EB]"><span className="font-bold text-[#1E40AF]">{pushResults.new_maintenance.pushed} pushed</span>{pushResults.new_maintenance.failed > 0 && <span className="ml-2 text-[#DC2626]">{pushResults.new_maintenance.failed} failed</span>}</div>}
            </div>

            <div className="bg-white border border-[#E7E5E4] p-5" data-testid="push-card-equipment">
              <div className="flex items-center gap-3 mb-2">
                <Wrench size={18} className="text-[#D97706]" />
                <div>
                  <h3 className="font-heading font-bold text-[#1C1917]">Equipment Updates</h3>
                  <p className="text-xs text-[#78716C]">Push modified machine records back to SAP</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handlePushToSAP("equipment_updates", "equipment-updates")} disabled={pushLoading.equipment_updates} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none flex-1" data-testid="push-equipment">
                  {pushLoading.equipment_updates ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push to SAP
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleCSVExport("equipment")} className="rounded-none border-[#E7E5E4]" data-testid="csv-export-equipment">
                  <Download size={14} className="mr-1" />CSV
                </Button>
              </div>
              {pushResults.equipment_updates && <div className="text-xs mt-2 p-2 bg-[#FFFBEB] border border-[#D97706]"><span className="font-bold text-[#92400E]">{pushResults.equipment_updates.pushed} pushed</span>{pushResults.equipment_updates.skipped > 0 && <span className="ml-2 text-[#78716C]">{pushResults.equipment_updates.skipped} unchanged</span>}</div>}
            </div>

            <div className="bg-white border border-[#E7E5E4] p-5" data-testid="push-card-parts">
              <div className="flex items-center gap-3 mb-2">
                <Package size={18} className="text-[#8B5CF6]" />
                <div>
                  <h3 className="font-heading font-bold text-[#1C1917]">Parts Usage</h3>
                  <p className="text-xs text-[#78716C]">Post goods movements for parts consumed in maintenance</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handlePushToSAP("parts_usage", "parts-usage")} disabled={pushLoading.parts_usage} className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white rounded-none flex-1" data-testid="push-parts">
                  {pushLoading.parts_usage ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push to SAP
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleCSVExport("materials")} className="rounded-none border-[#E7E5E4]" data-testid="csv-export-materials">
                  <Download size={14} className="mr-1" />CSV
                </Button>
              </div>
              {pushResults.parts_usage && <div className="text-xs mt-2 p-2 bg-[#F5F3FF] border border-[#8B5CF6]"><span className="font-bold text-[#5B21B6]">{pushResults.parts_usage.pushed} pushed</span>{pushResults.parts_usage.failed > 0 && <span className="ml-2 text-[#DC2626]">{pushResults.parts_usage.failed} failed</span>}</div>}
            </div>

            <div className="bg-white border border-[#E7E5E4] p-5" data-testid="push-card-workorders">
              <div className="flex items-center gap-3 mb-2">
                <ClipboardList size={18} className="text-[#059669]" />
                <div>
                  <h3 className="font-heading font-bold text-[#1C1917]">Work Orders</h3>
                  <p className="text-xs text-[#78716C]">Push completed work orders to SAP as maintenance notifications</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => handlePushToSAP("workorders", "workorders")} disabled={pushLoading.workorders} className="bg-[#059669] hover:bg-[#047857] text-white rounded-none flex-1" data-testid="push-workorders">
                  {pushLoading.workorders ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Upload size={14} className="mr-1" />}Push to SAP
                </Button>
              </div>
              {pushResults.workorders && <div className="text-xs mt-2 p-2 bg-[#ECFDF5] border border-[#059669]"><span className="font-bold text-[#065F46]">{pushResults.workorders.pushed} pushed</span>{pushResults.workorders.failed > 0 && <span className="ml-2 text-[#DC2626]">{pushResults.workorders.failed} failed</span>}</div>}
            </div>
          </div>
        </div>

        {/* Import History */}
        <div className="bg-white border border-[#E7E5E4] p-6" data-testid="sap-history-section">
          <div className="flex items-center gap-3 mb-4">
            <Clock size={20} className="text-[#D97706]" />
            <h2 className="font-heading font-bold text-xl text-[#1C1917]">Sync History</h2>
          </div>
          {history.length === 0 ? (
            <p className="text-[#78716C] text-sm py-4">No imports yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAF9] border-b-2 border-[#1C1917]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Source</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Imported</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Skipped</th>
                    <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Failed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E7E5E4]">
                  {history.map((h) => (
                    <tr key={h.id} className="hover:bg-[#FAFAF9]" data-testid={`history-row-${h.id}`}>
                      <td className="px-3 py-2 font-mono text-xs text-[#78716C]">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs font-bold bg-[#FEF3C7] text-[#92400E] uppercase">{h.import_type}</span></td>
                      <td className="px-3 py-2 font-mono text-xs">{h.source}</td>
                      <td className="px-3 py-2 text-[#10B981] font-bold">{h.records_imported}</td>
                      <td className="px-3 py-2 text-[#78716C]">{h.records_skipped}</td>
                      <td className="px-3 py-2 text-[#DC2626]">{h.records_failed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Export History */}
          {exportHistory.length > 0 && (
            <>
              <h3 className="font-heading font-bold text-[#1C1917] mt-6 mb-2">Push / Export History</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FAFAF9] border-b-2 border-[#1C1917]">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Type</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Target</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Pushed</th>
                      <th className="px-3 py-2 text-left text-xs font-bold text-[#1C1917] uppercase">Failed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E7E5E4]">
                    {exportHistory.map((h) => (
                      <tr key={h.id} className="hover:bg-[#FAFAF9]" data-testid={`export-row-${h.id}`}>
                        <td className="px-3 py-2 font-mono text-xs text-[#78716C]">{new Date(h.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2"><span className="px-2 py-0.5 text-xs font-bold bg-[#DBEAFE] text-[#1E40AF] uppercase">{h.export_type}</span></td>
                        <td className="px-3 py-2 font-mono text-xs">{h.target}</td>
                        <td className="px-3 py-2 text-[#10B981] font-bold">{h.records_pushed}</td>
                        <td className="px-3 py-2 text-[#DC2626]">{h.records_failed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
